import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Cookie, Mail, Database, UserCheck, Lock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-6" onClick={() => navigate('/')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: February 2, 2026</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                At beloud.pro, we take your privacy seriously. This Privacy Policy explains how we collect,
                use, disclose, and safeguard your information when you use our AI-powered LinkedIn growth platform.
              </p>
              <p>
                By using beloud.pro, you agree to the collection and use of information in accordance with this policy.
                We are committed to protecting your personal data and your right to privacy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="font-semibold">Personal Information:</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Email address and full name</li>
                <li>LinkedIn profile URLs you choose to track</li>
                <li>Payment information (processed securely by LemonSqueezy)</li>
                <li>Usage data and analytics</li>
              </ul>

              <h3 className="font-semibold mt-4">Generated Content:</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>AI-generated posts and comments</li>
                <li>Chat session history</li>
                <li>Persona configurations and preferences</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-primary" />
                Cookies and Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>We use cookies and similar tracking technologies to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Keep you logged in and maintain your session</li>
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve our services and user experience</li>
              </ul>
              <p className="mt-4">
                You can manage your cookie preferences through our cookie consent banner or your browser settings.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                Your Rights (GDPR)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>Under GDPR, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Right to Restrict Processing:</strong> Request limitation of data processing</li>
                <li><strong>Right to Data Portability:</strong> Receive data in a structured format</li>
                <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                We implement appropriate technical and organizational measures to protect your personal data:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Encryption at rest and in transit (TLS 1.3)</li>
                <li>Secure cloud infrastructure (AWS)</li>
                <li>Regular security audits and assessments</li>
                <li>Strict access controls and authentication</li>
                <li>Regular data backups</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>
                  Email:{' '}
                  <a href="mailto:admin@beloud.pro" className="text-primary hover:underline">
                    admin@beloud.pro
                  </a>
                </li>
                <li>Address: beloud.pro, Italy</li>
                <li>Privacy contact: admin@beloud.pro</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
